import { cn } from "@/modules/lib/utils"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { ProLargeIcon } from "@images/svgIcons/proLargeIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleItemEntity } from "@modules/programBundle/entities/programBundleEntity"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramType, SharedCoachStatus } from "@modules/programs/types/programTypes"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"
import styled from "styled-components"

type HuProgramMiniCardsProps = {
  item: UserProgramItemEntity | UserProgramDetailsEntity | ProgramBundleItemEntity
  disabled?: boolean
  editionAllowed?: boolean
} & React.ComponentProps<"div">

export function HuProgramMiniCards({
  item,
  disabled = false,
  className,
  editionAllowed = false,
  ...props
}: HuProgramMiniCardsProps) {
  const { navigateToProgramView } = useProgramSidebar()

  const itemIcon = useMemo(() => {
    if (item.type === ProgramType.VIRTUAL) {
      return (
        <InfinityIcon
          color="currentColor"
          width={15}
          height={15}
          className="shrink-0 fill-textOnColor-heading text-textOnColor-heading"
        />
      )
    } else if (item.type === ProgramType.PRO) {
      return (
        <ProLargeIcon
          color="currentColor"
          width={15}
          height={15}
          className="shrink-0 fill-textOnColor-heading text-textOnColor-heading"
        />
      )
    } else {
      return (
        <OnOffIcon
          color="currentColor"
          width={15}
          height={15}
          className="shrink-0 fill-textOnColor-heading text-textOnColor-heading"
        />
      )
    }
  }, [item])

  const onViewDetailsClick = () => {
    if (!disabled) {
      navigateToProgramView(item.id, editionAllowed, {
        leftButtonColor: "var(--surface-background)",
        replaceAllComponents: false,
      })
    }
  }

  return (
    <div
      onClick={onViewDetailsClick}
      className={cn(
        "relative flex h-32 w-40 flex-col justify-between overflow-hidden rounded-3xl p-1",
        !disabled && "cursor-pointer",
        className,
      )}
      {...props}
    >
      <div
        className="absolute inset-0 z-1"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(34,34,34,0) 33%, rgba(34,34,34,1) 100%), url(${item.image?.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      <div className="z-2 flex h-full flex-col justify-between">
        <div className="ml-3 pt-3">{itemIcon}</div>
        <Text fontStyle="LM" className="ml-3 pr-2 pb-3 text-textOnColor-heading">
          {item.name}
        </Text>
      </div>

      {item instanceof UserProgramItemEntity && item.sharedCoachStatus === SharedCoachStatus.PENDING && (
        <GradientOverlay>
          <ClockOutlineIcon color="currentColor" width={24} height={24} className="text-textOnColor-heading" />
          <Text fontStyle="TS" className="text-center text-textOnColor-heading">
            {word("program.sharedCoach.status.pendingValidation")}
          </Text>
        </GradientOverlay>
      )}
    </div>
  )
}

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-width: 0px;
  height: 100%;
  width: 100%;
  z-index: 1;
  background: linear-gradient(180deg, rgba(34, 34, 34, 0.62) 70%, #222 99.9%);
  backdrop-filter: blur(1px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  gap: 0.4rem;
`
